/**
 * useAutoBalancedSliders
 * For Media Devoted | Created By Ciro Autiero
 *
 * Hook that owns N integer sliders whose sum is always exactly 100. When
 * the user moves slider `i`, the inverse delta is redistributed across
 * the other sliders proportionally to their current values, then a final
 * largest-remainder pass keeps integers summing to 100.
 */

import { useCallback, useEffect, useState } from 'react';

import { rebalance } from './auto-balanced-sliders.math';

export interface UseAutoBalancedSlidersResult {
  values: Record<string, number>;
  setValue: (id: string, next: number) => void;
  reset: (next: Record<string, number>) => void;
}

export function useAutoBalancedSliders(
  initial: Record<string, number>,
): UseAutoBalancedSlidersResult {
  const [values, setValues] = useState<Record<string, number>>(() =>
    rebalance(initial, null),
  );

  useEffect(() => {
    setValues(rebalance(initial, null));
  }, [stableKey(initial)]);

  const setValue = useCallback((id: string, next: number) => {
    setValues((current) => {
      if (!(id in current)) return current;
      const target = clamp(next, 0, 100);
      return rebalance(current, { id, target });
    });
  }, []);

  const reset = useCallback((nextValues: Record<string, number>) => {
    setValues(rebalance(nextValues, null));
  }, []);

  return { values, setValue, reset };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function stableKey(values: Record<string, number>): string {
  return Object.keys(values)
    .sort()
    .map((k) => `${k}:${values[k]}`)
    .join('|');
}
